import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Clock, Mail, Instagram, Facebook } from "lucide-react";
import { RESTAURANT } from "../data/menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Couldn't send your message. Please try again or call us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="bg-[#0A0A0A] pt-[180px]" data-testid="contact-page">
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 md:px-12">
        <span className="text-[#D4A017] uppercase tracking-[0.3em] text-xs">Get in touch</span>
        <h1 className="font-display text-white text-5xl md:text-7xl mt-4 leading-[0.95] tracking-tight max-w-3xl">
          Let's <span className="italic font-accent text-[#D4A017]">talk</span>.
        </h1>
        <p className="text-white/65 mt-6 max-w-2xl text-lg">
          Reservations, private events, feedback or simply saying hello — we'd love to hear from you.
        </p>
      </section>

      <section className="pb-20 max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10">
        {/* Form */}
        <div className="md:col-span-7 bg-[#141414] border border-white/5 p-8 md:p-12">
          <h2 className="font-display text-white text-3xl mb-2">Send a message</h2>
          <p className="text-white/55 text-sm mb-8">We'll reply within 24 hours.</p>
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-white/60 text-xs uppercase tracking-[0.2em]">Name</label>
                <input
                  required
                  value={form.name}
                  onChange={update("name")}
                  className="w-full mt-2 bg-transparent border-b border-white/15 py-3 text-white focus:outline-none focus:border-[#D4A017]"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="text-white/60 text-xs uppercase tracking-[0.2em]">Email</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className="w-full mt-2 bg-transparent border-b border-white/15 py-3 text-white focus:outline-none focus:border-[#D4A017]"
                  data-testid="contact-email"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-white/60 text-xs uppercase tracking-[0.2em]">Phone (optional)</label>
                <input
                  value={form.phone}
                  onChange={update("phone")}
                  className="w-full mt-2 bg-transparent border-b border-white/15 py-3 text-white focus:outline-none focus:border-[#D4A017]"
                  data-testid="contact-phone"
                />
              </div>
              <div>
                <label className="text-white/60 text-xs uppercase tracking-[0.2em]">Subject</label>
                <input
                  value={form.subject}
                  onChange={update("subject")}
                  className="w-full mt-2 bg-transparent border-b border-white/15 py-3 text-white focus:outline-none focus:border-[#D4A017]"
                  data-testid="contact-subject"
                />
              </div>
            </div>
            <div>
              <label className="text-white/60 text-xs uppercase tracking-[0.2em]">Message</label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={update("message")}
                className="w-full mt-2 bg-transparent border-b border-white/15 py-3 text-white focus:outline-none focus:border-[#D4A017] resize-none"
                data-testid="contact-message"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="btn-primary disabled:opacity-60 mt-4"
              data-testid="contact-submit"
            >
              {sending ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>

        {/* Info */}
        <div className="md:col-span-5 space-y-5" data-testid="contact-info">
          <a
            href={`tel:${RESTAURANT.phoneRaw}`}
            className="bg-[#141414] border border-white/5 p-7 flex items-center gap-5 hover:border-[#D4A017]/40 transition-all group"
            data-testid="contact-call-card"
          >
            <div className="w-12 h-12 border border-[#D4A017] text-[#D4A017] flex items-center justify-center group-hover:bg-[#D4A017] group-hover:text-black transition-colors">
              <Phone size={18} />
            </div>
            <div>
              <div className="text-white/50 text-xs uppercase tracking-[0.2em]">Call</div>
              <div className="text-white text-lg font-medium">{RESTAURANT.phone}</div>
            </div>
          </a>

          <a
            href="mailto:info@aradelarestaurant.co.uk"
            className="bg-[#141414] border border-white/5 p-7 flex items-center gap-5 hover:border-[#D4A017]/40 transition-all group"
            data-testid="contact-email-card"
          >
            <div className="w-12 h-12 border border-white/20 text-white flex items-center justify-center group-hover:bg-white group-hover:text-black transition-colors">
              <Mail size={18} />
            </div>
            <div>
              <div className="text-white/50 text-xs uppercase tracking-[0.2em]">Email</div>
              <div className="text-white text-lg font-medium">info@aradelarestaurant.co.uk</div>
            </div>
          </a>

          <div className="bg-[#141414] border border-white/5 p-7" data-testid="contact-hours-card">
            <div className="flex items-center gap-3 mb-4">
              <Clock size={18} className="text-[#D4A017]" />
              <h3 className="font-display text-white text-xl">Opening Hours</h3>
            </div>
            <ul className="space-y-2 text-white/70 text-sm">
              {RESTAURANT.hours.map((h) => (
                <li key={h.day} className="flex justify-between border-b border-white/5 pb-2">
                  <span>{h.day}</span>
                  <span className="text-white/90">{h.time}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#141414] border border-white/5 p-7" data-testid="contact-address-card">
            <div className="flex items-center gap-3 mb-3">
              <MapPin size={18} className="text-[#D4A017]" />
              <h3 className="font-display text-white text-xl">Find Us</h3>
            </div>
            <p className="text-white/70 text-sm leading-relaxed">{RESTAURANT.address}</p>
            <div className="flex gap-3 mt-5">
              <a
                href={RESTAURANT.social.instagram}
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-[#D4A017] hover:border-[#D4A017] hover:text-black transition-all text-white"
                data-testid="contact-instagram"
                aria-label="Instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href={RESTAURANT.social.facebook}
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-[#D4A017] hover:border-[#D4A017] hover:text-black transition-all text-white"
                data-testid="contact-facebook"
                aria-label="Facebook"
              >
                <Facebook size={16} />
              </a>
              <a
                href={RESTAURANT.social.tiktok}
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 border border-white/15 flex items-center justify-center hover:bg-[#D4A017] hover:border-[#D4A017] hover:text-black transition-all text-white"
                data-testid="contact-tiktok"
                aria-label="TikTok"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9a8.16 8.16 0 0 0 4.77 1.52V7.1a4.85 4.85 0 0 1-1.84-.41z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24" data-testid="contact-map">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="aspect-video w-full">
            <iframe
              title="Aradela location"
              src={RESTAURANT.mapsEmbed}
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.4) contrast(1.05) brightness(0.85)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map-iframe"
            />
          </div>
        </div>
      </section>
    </main>
  );
}
